import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllReactions,
  getMoleculeRender,
  getMoleculeSVG,
  getUserOrganizations,
} from "../../utils/Api";
import { FlaskConical, ArrowRight } from "lucide-react";
import DocumentSelect from "../../components/DocumentSelect";
import SmilesExportButton from "../../components/SmilesExportButton";
import { SmilesExportFilters } from "../../hooks/useSmilesExport";
import { slugify } from "../../utils/slug";

interface MoleculeRef {
  smiles: string;
  graph?: { symbols: string[]; coords: number[][]; edges: number[][] } | null;
  bbox_width?: number | null;
  bbox_height?: number | null;
}

interface ReactionItem {
  reactants: MoleculeRef[];
  products: MoleculeRef[];
  conditions: string[];
  document_id: string;
  document_name: string;
  page_id: string;
}

interface OrgInfo {
  id: string;
  name: string;
  role: string;
}

type TabKey = "all" | "personal" | string; // string = org id

const PAGE_SIZE = 24;

const MolThumb: React.FC<{ mol: MoleculeRef }> = ({ mol }) => {
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [inView, setInView] = useState(false);
  const thumbRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = thumbRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "150px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    let objectUrl = "";

    const load = async () => {
      try {
        if (mol.graph?.symbols?.length) {
          const renderParams: Parameters<typeof getMoleculeRender>[0] = {
            symbols: mol.graph.symbols,
            coords: mol.graph.coords,
            edges: mol.graph.edges,
            force_aspect: true,
          };
          if (mol.bbox_width) renderParams.bbox_width = mol.bbox_width;
          if (mol.bbox_height) renderParams.bbox_height = mol.bbox_height;
          const res = await getMoleculeRender(renderParams);
          if (cancelled) return;
          const url = URL.createObjectURL(res.data as Blob);
          objectUrl = url;
          setImgUrl(url);
        } else if (mol.smiles) {
          const res = await getMoleculeSVG(mol.smiles, {
            zoom: "1.0",
            w: "120",
            h: "100",
          });
          if (cancelled) return;
          const raw = res.data;
          const svg =
            typeof raw === "string" && raw.trim().startsWith("<svg")
              ? raw
              : raw?.svg;
          if (svg) {
            setImgUrl(
              `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
            );
          } else {
            setError(true);
          }
        } else {
          setError(true);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [inView, mol.smiles, mol.graph]);

  return (
    <div ref={thumbRef} className="w-[72px] h-[60px]">
      {error ? (
        <div className="w-full h-full bg-gray-100 rounded flex items-center justify-center">
          <FlaskConical size={18} className="text-gray-300" />
        </div>
      ) : imgUrl ? (
        <img
          src={imgUrl}
          alt="molecule"
          className="w-full h-full object-contain"
        />
      ) : (
        <div className="w-full h-full bg-gray-100 animate-pulse rounded" />
      )}
    </div>
  );
};

const ReactionCard: React.FC<{ reaction: ReactionItem }> = ({ reaction }) => {
  const navigate = useNavigate();
  const allMols = [...reaction.reactants, ...reaction.products];
  const hasContent = allMols.length > 0;

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-[#1d365d]/40 hover:shadow-md transition-all duration-150 flex flex-col cursor-pointer"
      onClick={() => navigate(`/view/${reaction.document_id}`)}
    >
      <div className="flex-1 flex items-center justify-center gap-2 p-4 min-h-[100px] flex-wrap">
        {hasContent ? (
          <>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              {reaction.reactants.slice(0, 3).map((mol, i) => (
                <MolThumb key={i} mol={mol} />
              ))}
              {reaction.reactants.length > 3 && (
                <span className="text-xs text-gray-400">
                  +{reaction.reactants.length - 3}
                </span>
              )}
            </div>

            <div className="flex flex-col items-center gap-0.5 shrink-0">
              {reaction.conditions.length > 0 && (
                <span className="text-[10px] text-gray-400 max-w-[80px] truncate text-center">
                  {reaction.conditions[0]}
                </span>
              )}
              <ArrowRight size={18} className="text-[#1d365d]/50" />
            </div>

            <div className="flex items-center gap-1 flex-wrap justify-center">
              {reaction.products.slice(0, 3).map((mol, i) => (
                <MolThumb key={i} mol={mol} />
              ))}
              {reaction.products.length > 3 && (
                <span className="text-xs text-gray-400">
                  +{reaction.products.length - 3}
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1 text-gray-300">
            <FlaskConical size={28} />
            <span className="text-xs">No preview</span>
          </div>
        )}
      </div>

      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/60">
        <p
          className="text-xs text-[#1d365d]/70 truncate text-center"
          title={reaction.document_name}
        >
          {reaction.document_name}
        </p>
      </div>
    </div>
  );
};

const Reactions: React.FC = () => {
  const [reactions, setReactions] = useState<ReactionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    getUserOrganizations().then((res) => {
      if (cancelled) return;
      setOrgs(res.data.organizations || []);
    });
    return () => { cancelled = true; };
  }, []);

  const fetchReactions = useCallback(
    async (offset: number, append: boolean, docId: string, tab: TabKey) => {
      try {
        if (offset === 0) setLoading(true);
        else setLoadingMore(true);

        const params: Parameters<typeof getAllReactions>[0] = {
          limit: PAGE_SIZE,
          offset,
        };
        if (docId) params.document_id = docId;

        if (tab === "personal") {
          params.source = "personal";
        } else if (tab !== "all") {
          // tab is an org id
          params.organisation_id = tab;
        }

        const { data } = await getAllReactions(params);

        if (append) {
          setReactions((prev) => [...prev, ...data.reactions]);
        } else {
          setReactions(data.reactions);
        }
        setTotal(data.total);
        setError(null);
      } catch (err: any) {
        setError(err.message ?? "Failed to load reactions");
        if (!append) setReactions([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchReactions(0, false, selectedDoc?.id ?? "", activeTab);
  }, [fetchReactions, selectedDoc, activeTab]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: "all", label: "All" },
    { key: "personal", label: "My Documents" },
    ...orgs.map((org: OrgInfo) => ({ key: org.id, label: org.name })),
  ];

  const remaining = total - reactions.length;

  const exportFilters: SmilesExportFilters = {};
  if (selectedDoc?.id) exportFilters.document_id = selectedDoc.id;
  if (activeTab === "personal") {
    exportFilters.source = "personal";
  } else if (activeTab !== "all") {
    exportFilters.organisation_id = activeTab;
  }

  const exportScope = selectedDoc
    ? `doc-${slugify(selectedDoc.name)}`
    : activeTab === "all"
      ? "all"
      : activeTab === "personal"
        ? "personal"
        : `org-${slugify(orgs.find((o) => o.id === activeTab)?.name ?? activeTab)}`;

  useEffect(() => {
    if (remaining <= 0 || loadingMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchReactions(reactions.length, true, selectedDoc?.id ?? "", activeTab);
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [remaining, loadingMore, reactions.length, fetchReactions, selectedDoc, activeTab]);

  return (
    <div className="bg-white h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-8 sm:pt-12 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">
              Reactions
            </h3>
            <p className="text-base text-gray-500 mt-0.5">
              {loading
                ? "Loading reactions…"
                : `${total.toLocaleString()} reaction${total !== 1 ? "s" : ""} identified across all documents`}
            </p>
          </div>

          <SmilesExportButton
            mode="reactions"
            filters={exportFilters}
            scopeSlug={exportScope}
            disabled={total === 0}
          />
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 mb-5 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setSelectedDoc(null);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors duration-100 border-b-2 -mb-px ${
                activeTab === tab.key
                  ? "border-[#1d365d] text-[#1d365d]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mb-6">
          <DocumentSelect value={selectedDoc} onChange={setSelectedDoc} />
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2.5 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
            {error}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 pb-10">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                <div className="h-[100px] bg-gray-100 animate-pulse" />
                <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/60">
                  <div className="h-3 bg-gray-200 animate-pulse rounded w-2/3 mx-auto" />
                </div>
              </div>
            ))}
          </div>
        ) : reactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <FlaskConical size={40} className="text-gray-300 mb-3" />
            <p className="font-medium text-gray-600 mb-1">
              {selectedDoc
                ? `No reactions found in "${selectedDoc.name}"`
                : "No reactions yet"}
            </p>
            <p className="text-sm text-gray-400">
              {selectedDoc
                ? "Try a different document"
                : activeTab !== "all" && activeTab !== "personal"
                ? "No reactions found in this organisation's documents"
                : "Upload documents containing chemical reactions to get started"}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {reactions.map((rxn, i) => (
                <ReactionCard key={`${rxn.page_id}-${i}`} reaction={rxn} />
              ))}
            </div>

            <div ref={sentinelRef} className="mt-8 flex flex-col items-center gap-2 py-4">
              {loadingMore && (
                <>
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-[#1d365d] rounded-full animate-spin" />
                  <p className="text-sm text-gray-400">
                    {reactions.length} of {total} reactions
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Reactions;
